import { combineReducers } from 'redux';
import { generateUniqueId } from 'utils/unique-id';

import { removeNulls } from 'utils/Object.utils';

import variablesReducer, {
  type VariablesState,
} from 'reducers/portfolio.variables.reducer';
import propertiesReducer, {
  type PropertiesState,
} from 'reducers/portfolio.properties.reducer';
import expensesReducer, {
  newInitialExpenses,
  type ExpensesState,
} from 'reducers/portfolio.expenses.reducer';
import investorsReducer, {
  type InvestorsState,
} from 'reducers/portfolio.investors.reducer';

type ReducerAction = {
  type?: string;
  [key: string]: any;
};

export type PortfolioMeta = {
  name?: string;
  description?: string;
  createdOn?: string;
  projectionHash?: unknown;
  [key: string]: unknown;
};

export type Portfolio = {
  id?: string;
  meta?: PortfolioMeta;
  variables?: VariablesState | Record<string, any>;
  properties?: PropertiesState | Record<string, any>;
  expenses?: ExpensesState | Record<string, any>;
  investors?: InvestorsState | Record<string, any>;
  [key: string]: any;
};

export type PortfolioStatus = {
  isLoading?: boolean;
  error?: { code: string; [key: string]: unknown } | null;
  metaError?: { code: string; [key: string]: unknown } | null;
  propertyErrors?: Record<
    string,
    { code: string; [key: string]: unknown } | null
  >;
  investorErrors?: Record<
    string,
    { code: string; [key: string]: unknown } | null
  >;
  [key: string]: unknown;
};

const initAction: ReducerAction = { type: 'INIT' };

const basePortfolio = (): Portfolio => {
  return {
    id: generateUniqueId(),
    meta: {
      name: 'My Portfolio',
      description: 'My property investment portfolio plan',
      createdOn: new Date().toISOString(),
    },
    variables: variablesReducer(undefined, initAction),
    properties: propertiesReducer(undefined, initAction),
    expenses: expensesReducer(undefined, initAction),
    investors: investorsReducer(undefined, initAction),
  };
};

/**
 * newPortfolio - constructs a new portfolio object with the give properties
 * @param props - props to be merged with default object. Optional
 * @return new portfolio object
 */
export const newPortfolio = (props: Partial<Portfolio> = {}): Portfolio => {
  const expenses = newInitialExpenses();
  return {
    ...basePortfolio(),
    expenses,
    ...props,
  };
};

const portfoliosById = (
  stateArg: Record<string, Portfolio> | undefined,
  action: ReducerAction
): Record<string, Portfolio> => {
  const state = stateArg || {};
  switch (action.type) {
    case 'AUTH_LOGOUT':
    case 'AUTH_LOGOUT_SUCCESS': {
      return {};
    }
    case 'PORTFOLIOS_ADD_NEW': {
      const { portfolio } = action;
      return {
        ...state,
        [portfolio.id]: portfolio,
      };
    }
    case 'PORTFOLIOS_DELETE': {
      // TODO: Propagate the deletion to remove related items
      const { portfolioId } = action;
      return removeNulls({
        ...state,
        [portfolioId]: null,
      }) as Record<string, Portfolio>;
    }
    case 'PORTFOLIO_FINISHED_LOADING': {
      const { portfolio } = action;
      return {
        ...state,
        [portfolio.id]: portfolio,
      };
    }
    case 'PORTFOLIO_META_UPDATE': {
      const { portfolioId, portfolioMeta } = action;
      const portfolio = state[portfolioId];
      if (!portfolio) return state;
      return {
        ...state,
        [portfolioId]: {
          ...portfolio,
          meta: {
            ...portfolio.meta,
            ...portfolioMeta,
          },
        },
      };
    }
    case 'PORTFOLIO_PROJECTION_UPDATE': {
      const { portfolioId, projection } = action;
      const portfolio = (state[portfolioId] || {}) as Portfolio;
      const projectionHash = projection.hash;
      return {
        ...state,
        [portfolio.id]: {
          ...portfolio,
          meta: {
            ...portfolio.meta,
            projectionHash,
          },
        },
      };
    }
    case 'PORTFOLIO_META_FINISHED_UPDATING_WITH_ERROR': {
      // Roll back the optimistic PORTFOLIO_META_UPDATE for this portfolio's
      // cached entry to the meta that was actually last persisted (captured
      // before the optimistic update - see updatePortfolioMeta in
      // portfolios.actions.js). Mirrors the equivalent
      // LEDGER_META_FINISHED_UPDATING_WITH_ERROR case in ledgers.reducer.js
      // (Bug 4: this case didn't exist here at all - a permanently-failed
      // portfolio rename could never be rolled back, leaving the settings
      // field showing the new, never-saved value indefinitely, unlike
      // ledgers' equivalent). This action's id/previousMeta are nested
      // inside action.payload (unlike most other actions here, which carry a
      // top-level action.portfolioId), so it falls outside the default case
      // below and needs its own handling.
      const { portfolioId, previousMeta } = action.payload || {};
      const portfolio = state[portfolioId];
      if (!portfolio || !previousMeta) return state;
      return {
        ...state,
        [portfolioId]: {
          ...portfolio,
          meta: previousMeta,
        },
      };
    }
    default: {
      const { portfolioId } = action;
      const portfolio = state[portfolioId];
      if (portfolio) {
        return {
          ...state,
          [portfolioId]: {
            ...portfolio,
            variables: variablesReducer(portfolio.variables, action),
            properties: propertiesReducer(portfolio.properties, action),
            investors: investorsReducer(portfolio.investors, action),
            expenses: expensesReducer(portfolio.expenses, action),
          },
        };
      }
      return state;
    }
  }
};

const allPortfolios = (
  stateArg: string[] | undefined,
  action: ReducerAction
): string[] => {
  const state = stateArg || [];
  switch (action.type) {
    case 'AUTH_LOGOUT':
    case 'AUTH_LOGOUT_SUCCESS': {
      return [];
    }
    case 'PORTFOLIOS_ADD_NEW': {
      const { portfolio } = action;
      return state.concat([portfolio.id]);
    }
    case 'PORTFOLIOS_DELETE': {
      const { portfolioId } = action;
      return state.filter((id) => {
        return id !== portfolioId;
      });
    }
    case 'PORTFOLIO_FINISHED_LOADING': {
      const { portfolio } = action;
      return [
        ...state.filter((id) => {
          return id !== portfolio.id;
        }),
        portfolio.id,
      ];
    }
    default:
      return state;
  }
};

const statusReducer = (
  stateArg: Record<string, PortfolioStatus> | undefined,
  action: ReducerAction
): Record<string, PortfolioStatus> => {
  const state = stateArg || {};
  switch (action.type) {
    case 'AUTH_LOGOUT':
    case 'AUTH_LOGOUT_SUCCESS': {
      return {};
    }
    case 'PORTFOLIO_IS_LOADING': {
      const { portfolioId } = action;
      return {
        ...state,
        [portfolioId]: {
          isLoading: true,
        },
      };
    }
    case 'PORTFOLIO_FINISHED_LOADING': {
      const { portfolio } = action;
      return {
        ...state,
        [portfolio.id]: {
          isLoading: false,
          error: null,
        },
      };
    }
    case 'PORTFOLIO_FINISHED_LOADING_WITH_ERROR': {
      const { portfolioId, error } = action;
      return {
        ...state,
        [portfolioId]: {
          error: error || { code: 'Unknown' },
          isLoading: false,
        },
      };
    }
    case 'PORTFOLIO_META_IS_UPDATING': {
      // Clears any previously-surfaced metaError as soon as a NEW attempt
      // starts (e.g. the user edits the field again after a failure) - see
      // didUpdatingPortfolioMetaErrored (portfolios.selector.js). Without
      // this, a stale error from an earlier failed edit could keep showing
      // even after a subsequent edit succeeds.
      const { portfolioId } = action;
      if (!portfolioId) return state;
      const existing = state[portfolioId] || {};
      return {
        ...state,
        [portfolioId]: { ...existing, metaError: null },
      };
    }
    case 'PORTFOLIO_META_FINISHED_UPDATING': {
      const { portfolioId } = action;
      const existing = state[portfolioId] || {};
      return {
        ...state,
        [portfolioId]: { ...existing, metaError: null },
      };
    }
    case 'PORTFOLIO_META_FINISHED_UPDATING_WITH_ERROR': {
      // Defense in depth for the ACL-population race (see
      // utils/firebase/retryOnPermissionDenied.js) - by the time this
      // dispatches, firebase_updatePortfolioMeta's bounded retry has already
      // been exhausted (or the error was never retryable to begin with), so
      // this really is a failure the user needs to see, not a transient blip
      // that will self-heal. `portfolioId` is nested inside action.payload
      // (mirrors the equivalent LEDGER_META_FINISHED_UPDATING_WITH_ERROR case
      // in ledgers.reducer.js), so this falls outside the default case below.
      const { portfolioId, error } = action.payload || {};
      if (!portfolioId) return state;
      const existing = state[portfolioId] || {};
      return {
        ...state,
        [portfolioId]: { ...existing, metaError: error || { code: 'Unknown' } },
      };
    }
    case 'PORTFOLIO_META_ERROR_DISMISSED': {
      const { portfolioId } = action;
      if (!portfolioId || !state[portfolioId]) return state;
      return {
        ...state,
        [portfolioId]: { ...state[portfolioId], metaError: null },
      };
    }
    case 'PORTFOLIO_PROPERTY_IS_UPDATING': {
      // Clears any previously-surfaced error for THIS property as soon as a
      // NEW attempt starts - see didUpdatingPropertyErrored
      // (selectors/portfolio.properties.selector.js). Nested under
      // propertyErrors (keyed by propertyId) rather than a flat field,
      // since - unlike portfolio meta - multiple properties on the same
      // portfolio can each be mid-edit/failed independently.
      const { portfolioId, propertyId } = action;
      if (!portfolioId || !propertyId) return state;
      const existing = state[portfolioId] || {};
      const propertyErrors = existing.propertyErrors || {};
      return {
        ...state,
        [portfolioId]: {
          ...existing,
          propertyErrors: { ...propertyErrors, [propertyId]: null },
        },
      };
    }
    case 'PORTFOLIO_PROPERTY_FINISHED_UPDATING': {
      const { portfolioId, propertyId } = action;
      if (!portfolioId || !propertyId) return state;
      const existing = state[portfolioId] || {};
      const propertyErrors = existing.propertyErrors || {};
      return {
        ...state,
        [portfolioId]: {
          ...existing,
          propertyErrors: { ...propertyErrors, [propertyId]: null },
        },
      };
    }
    case 'PORTFOLIO_PROPERTY_FINISHED_UPDATING_WITH_ERROR': {
      // Defense in depth for the ACL-population race (see
      // utils/firebase/retryOnPermissionDenied.js) - by the time this
      // dispatches, firebase_updateProperty's bounded retry has already been
      // exhausted (or the error was never retryable to begin with), so this
      // really is a failure the user needs to see, not a transient blip that
      // will self-heal.
      const { portfolioId, propertyId, error } = action.payload || {};
      if (!portfolioId || !propertyId) return state;
      const existing = state[portfolioId] || {};
      const propertyErrors = existing.propertyErrors || {};
      return {
        ...state,
        [portfolioId]: {
          ...existing,
          propertyErrors: {
            ...propertyErrors,
            [propertyId]: error || { code: 'Unknown' },
          },
        },
      };
    }
    case 'PORTFOLIO_PROPERTY_ERROR_DISMISSED': {
      const { portfolioId, propertyId } = action;
      if (!portfolioId || !propertyId || !state[portfolioId]) return state;
      const existing = state[portfolioId];
      const propertyErrors = existing.propertyErrors || {};
      return {
        ...state,
        [portfolioId]: {
          ...existing,
          propertyErrors: { ...propertyErrors, [propertyId]: null },
        },
      };
    }
    case 'PORTFOLIO_INVESTOR_IS_UPDATING': {
      // Mirrors PORTFOLIO_PROPERTY_IS_UPDATING above - clears any
      // previously-surfaced error for THIS investor as soon as a NEW attempt
      // starts (see didUpdatingInvestorErrored,
      // selectors/portfolio.investors.selector.js).
      const { portfolioId, investorId } = action;
      if (!portfolioId || !investorId) return state;
      const existing = state[portfolioId] || {};
      const investorErrors = existing.investorErrors || {};
      return {
        ...state,
        [portfolioId]: {
          ...existing,
          investorErrors: { ...investorErrors, [investorId]: null },
        },
      };
    }
    case 'PORTFOLIO_INVESTOR_FINISHED_UPDATING': {
      const { portfolioId, investorId } = action;
      if (!portfolioId || !investorId) return state;
      const existing = state[portfolioId] || {};
      const investorErrors = existing.investorErrors || {};
      return {
        ...state,
        [portfolioId]: {
          ...existing,
          investorErrors: { ...investorErrors, [investorId]: null },
        },
      };
    }
    case 'PORTFOLIO_INVESTOR_FINISHED_UPDATING_WITH_ERROR': {
      // Defense in depth for the ACL-population race (see
      // utils/firebase/retryOnPermissionDenied.js) - by the time this
      // dispatches, firebase_updateInvestor's bounded retry has already been
      // exhausted (or the error was never retryable to begin with), so this
      // really is a failure the user needs to see, not a transient blip that
      // will self-heal.
      const { portfolioId, investorId, error } = action.payload || {};
      if (!portfolioId || !investorId) return state;
      const existing = state[portfolioId] || {};
      const investorErrors = existing.investorErrors || {};
      return {
        ...state,
        [portfolioId]: {
          ...existing,
          investorErrors: {
            ...investorErrors,
            [investorId]: error || { code: 'Unknown' },
          },
        },
      };
    }
    case 'PORTFOLIO_INVESTOR_ERROR_DISMISSED': {
      const { portfolioId, investorId } = action;
      if (!portfolioId || !investorId || !state[portfolioId]) return state;
      const existing = state[portfolioId];
      const investorErrors = existing.investorErrors || {};
      return {
        ...state,
        [portfolioId]: {
          ...existing,
          investorErrors: { ...investorErrors, [investorId]: null },
        },
      };
    }
    default: {
      return state;
    }
  }
};

const portfoliosReducerCombined = combineReducers({
  byId: portfoliosById,
  allIds: allPortfolios,
  status: statusReducer,
});

export type PortfoliosState = ReturnType<typeof portfoliosReducerCombined>;

const portfoliosReducer = (
  state: PortfoliosState | undefined,
  action: ReducerAction
): PortfoliosState => {
  return portfoliosReducerCombined(state, action as never);
};

export default portfoliosReducer;
